import { datadogLogs } from '@datadog/browser-logs'
import { StoryblokComponent } from '@storyblok/react'
import { useTranslation } from 'next-i18next'
import { useMemo } from 'react'
import { Card } from 'ui/src/components/Card/Card'
import { Dialog } from 'ui/src/components/Dialog/Dialog'
import { LargeDialog } from 'ui/src/components/Dialog/LargeDialog'
import { Text } from 'ui/src/components/Text/Text'
import { yStack } from 'ui/src/patterns/stack'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import type { ProductOfferFragment } from '@/services/graphql/generated'
import { type Money } from '@/utils/formatter'
import { useFormatter } from '@/utils/useFormatter'
import { usePriceCalculatorDeductibleInfo } from '../../priceCalculatorAtoms'
import { CardRadioGroup } from '../CardRadioGroup/CardRadioGroup'
import { textTransition, vars } from '../CardRadioGroup/CardRadioGroup.css'
import { InfoButton } from '../InfoButton/InfoButton'
import { dialogContent } from './DeductibleSelectorV2.css'

type Deductible = {
  id: string
  price: Money
  title: string
  description: string
}

type Props = {
  offers: Array<ProductOfferFragment>
  selectedOffer: ProductOfferFragment
  onValueChange: (offerId: string) => void
}

export function DeductibleSelectorV2({ offers, selectedOffer, onValueChange }: Props) {
  const formatter = useFormatter()

  const deductibleLevels = useMemo(() => {
    const levels: Array<Deductible> = []

    offers.forEach((offer) => {
      if (offer.deductible) {
        levels.push({
          id: offer.id,
          price: offer.cost.net,
          title: offer.deductible.displayName,
          description: offer.deductible.tagline,
        })
      } else {
        datadogLogs.logger.warn(`Offer ${offer.id} has no deductible`)
      }
    })

    return levels
  }, [offers])

  return (
    <>
      <CardRadioGroup.Root value={selectedOffer.id} onValueChange={onValueChange}>
        {deductibleLevels.map((item, index) => {
          const isSelected = selectedOffer.id === item.id
          return (
            <CardRadioGroup.Item
              // NOTE: We want to avoid remounting selected deductible element when changing tiers
              // to avoid extra animation, hence index key
              key={index}
              className={yStack({ gap: 'lg' })}
              value={item.id}
            >
              <Card.Aside>
                <CardRadioGroup.Badge isSelected={isSelected}>
                  {formatter.monthlyPrice(item.price)}
                </CardRadioGroup.Badge>
              </Card.Aside>
              <Card.Header>
                <Card.Heading>
                  <Card.Title
                    as="h2"
                    className={textTransition}
                    variant="standard.24"
                    style={{ color: vars.titleColor }}
                  >
                    {item.title}
                  </Card.Title>
                </Card.Heading>
              </Card.Header>

              {item.description && (
                <Text className={textTransition} size="md" color={isSelected ? 'textTertiary' : 'textSecondary'}>
                  {item.description}
                </Text>
              )}
            </CardRadioGroup.Item>
          )
        })}
      </CardRadioGroup.Root>
      <DeductibleInfo />
    </>
  )
}

function DeductibleInfo() {
  const { t } = useTranslation('purchase-form')
  const deductibleInfo = usePriceCalculatorDeductibleInfo()
  return (
    <LargeDialog.Root>
      <Dialog.Trigger asChild={true}>
        <InfoButton>
          <div className={yStack()}>
            <Text as="div" size="xs">
              {t('DEDUCTIBLE_SELECTOR_DIALOG_TRIGGER')}
            </Text>
            <Text as="span" color="textSecondary" size="xs" balance={true}>
              {t('DEDUCTIBLE_SELECTOR_DIALOG_DESCRIPTION')}
            </Text>
          </div>
        </InfoButton>
      </Dialog.Trigger>
      <LargeDialog.Content className={dialogContent}>
        <Dialog.Title className={sprinkles({ display: 'none' })}>{t('DEDUCTIBLE_SELECTOR_FOOTER_LINK')}</Dialog.Title>
        {deductibleInfo.map((blok) => (
          <StoryblokComponent key={blok._uid} blok={blok} nested={true} />
        ))}
      </LargeDialog.Content>
    </LargeDialog.Root>
  )
}
